import { makeAutoObservable } from 'mobx';
import { DataStore } from './DataStore';
import { RegisterPageLibrary } from './libraries/RegisterPageLibrary';
import { InfoPageLibrary } from './libraries/InfoPageLibrary';
import { FilesPageLibrary } from './libraries/FilesPageLibrary';
import { StatementPageLibrary } from './libraries/StatementPageLibrary';
import { DeleteFormCallbackEnum, ModalLibrary, ModalTypeEnum } from './libraries/ModalLibrary';
import { StatementStatusEnum } from '../models/StatementStatusEnum';
import
{
	AxiosResponse
	, AxiosError
} from 'axios';
import { SaveDataResponse } from '../models/SaveDataResponse';
import { ErrorResponse, FileStateEnum, StatementModel, StatementTypeEnum } from '../models';
import { FileLibrary } from './libraries/FileLibrary';
import * as H from 'history';
import * as DateFNS from "date-fns";
import { YandexCaptchaStore } from './YandexCaptchaStore';

export class UIStore
{
	private dataStore: DataStore;

	public register: RegisterPageLibrary;
	public modal: ModalLibrary;
	public info: InfoPageLibrary;
	public files: FilesPageLibrary;
	public stat: StatementPageLibrary;

	public captcha: YandexCaptchaStore;

	constructor(store: DataStore)
	{
		this.dataStore = store;
		this.modal = new ModalLibrary(this);
		this.register = new RegisterPageLibrary(store, this);
		this.info = new InfoPageLibrary(this);
		this.files = new FilesPageLibrary(store, this);
		this.stat = new StatementPageLibrary(store, this);
		this.captcha = new YandexCaptchaStore();

		makeAutoObservable(this,
			{
				register: false,
				modal: false
			}, { autoBind: true });
	}

	public History: H.History<H.LocationState> | undefined = undefined;
	setHistory(value: H.History<H.LocationState>) { this.History = value; }

	public Search: string | undefined = undefined;
	setSearch(value: string) { this.Search = value; }

	public IsLoading: boolean = true;
	setIsLoading(value: boolean) { this.IsLoading = value; }

	public IsHamburgerOpened: boolean = false;
	setIsHamburgerOpened(value: boolean) { this.IsHamburgerOpened = value; }

	public IsDisabled: boolean = false;
	setIsDisabled(value: boolean)
	{
		this.IsDisabled = value;
	}

	public FillButtons: boolean = false;
	setFillButtons(value: boolean)
	{
		this.FillButtons = value;
	}

	// #region Navigation Tabs

	setActiveTab(value: number)
	{
		if (this.History)
		{
			switch (value)
			{
				case 0: this.History.push('/' + this.Search); break;
				case 1: this.History.push('/register' + this.Search); break;
				case 2: this.History.push('/info' + this.Search); break;
				case 3: this.History.push('/files' + this.Search); break;
				case 4: this.History.push('/statement' + this.Search); break;
			}
		}
	}
	changeActiveTab(value: number)
	{
		// Переключаться на страницу регистрации можно всегда
		if (value === 1)
		{
			// переключаем закладку
			this.setActiveTab(value);
			return;
		}
		// если почта не подтверждена, переключение невозможно
		if (!this.register.FeedbackEmail.IsConfirmed) return;
		// запускаем валидацию закладок
		this.register.Validate(value !== 4);
		this.info.Validate(value !== 4);
		this.files.Validate(value !== 4);
		// нельзя уйти дальше второй закладки если нет согласия на обработку перс. данных
		if (value > 2 && !this.info.AgreementCheck.Value) return;

		// если ничего не менялось
		if (!this.isStatementModified())
		{
			// переключаем закладку
			this.setActiveTab(value);
			return;
		}

		// если форма была уже создана
		if (this.StatementStatus !== StatementStatusEnum.NotCreated)
		{
			this.modal.SetChangingActiveTab(value);
			this.modal.SetChangeFormAction(DeleteFormCallbackEnum.ChangingActiveTab);
			this.modal.setShowModal(true, ModalTypeEnum.DeleteFormRequired, "Данные изменились", "Вы уверены, что хотите изменить данные?<br />Сформированное ранее заявление будет удалено из раздела “Заявление оператора ЭДО”.");
			return;
		}

		this.changeActiveTabEx(value);
	}

	changeActiveTabEx(value: number)
	{
		// запускаем сохранение
		this.modal.setShowModal(true, ModalTypeEnum.Spinner, 'Сохранение данных');
		this.dataStore.SaveData()
			// в случае успешного сохранения
			.then((res: AxiosResponse<SaveDataResponse>) =>
			{
				// выключаем спиннер
				this.modal.setShowModal(false);
				// если заявка не приехала, то ругаемся про неуспешное сохранение
				if (!res.data.Statement)
				{
					this.modal.setShowModal(true, ModalTypeEnum.Error, "Сохранение данных", "Сохранение данных завершилась ошибкой.");
					return;
				}
				// записываем приехавший объект
				this.dataStore.setStatement(res.data.Statement);
				// парсим приехавший объект
				this.dataStore.UpdateSatementFromServer(false);
				//// если форма была удалена - сообщаем
				//if (res.data.FormDeleted) this.modal.setShowModal(true, ModalTypeEnum.Error, "Сохранение данных", "Сформированное заявление удалено");
				// переключаем закладку
				this.setActiveTab(value);
			})
			// в случае неуспешного сохранения
			.catch((res: AxiosError) =>
			{
				// выключаем спиннер
				this.modal.setShowModal(false);
				// Ругаемся
				if (res.response?.data)
				{
					let error = (res.response.data as ErrorResponse);
					this.modal.setShowModal(true, ModalTypeEnum.Error, "Сбой сохранения данных", error.MessageToUser + " (" + error.ActivityId + ")");
					return;
				}
				else this.modal.setShowModal(true, ModalTypeEnum.Error, "Сохранение данных", "Сохранение данных завершилась ошибкой.");
			});
	}

	SendCheckMail()
	{
		if (!this.register.Validate()) return;

		// если ничего не менялось
		if (!this.isStatementModified())
		{
			// Посылаем почту
			this.SendCheckMailEx();
			return;
		}

		// если форма была уже создана
		if (this.StatementStatus !== StatementStatusEnum.NotCreated)
		{
			this.modal.SetChangeFormAction(DeleteFormCallbackEnum.SendingCheckMail);
			this.modal.setShowModal(true, ModalTypeEnum.DeleteFormRequired, "Данные изменились", "Вы уверены, что хотите изменить данные?<br />Сформированное ранее заявление будет удалено из раздела “Заявление оператора ЭДО”.");
			return;
		}

		// Посылаем почту
		this.SendCheckMailEx();
	}

	SendCheckMailEx()
	{
		this.captcha.Solve((token: string) =>
		{
			this.register.sendCheckMail(token);
		});
	}


	public DisabledTabs: number[] = [2, 3, 4];
	setDisabledTabs(value: number[]) { this.DisabledTabs = value; }

	// #endregion

	// #region Navigation Buttons

	PreviousButtonClick(currentPage: number)
	{
		if (currentPage < 2) return;
		if (currentPage === 4)
		{
			this.setActiveTab(currentPage - 1);
			return;
		}
		this.changeActiveTab(currentPage - 1);
	}

	NextButtonClick(currentPage: number)
	{
		if (currentPage < 4)
		{
			this.changeActiveTab(currentPage + 1);
			return;
		}

		if (this.StatementStatus === StatementStatusEnum.NotCreated)
		{
			if (!this.register.IsValid || !this.info.IsValid || !this.files.IsValid) return;

			this.modal.setShowModal(true, ModalTypeEnum.Spinner, 'Формирование заявления');

			this.dataStore.CreateForm()
				// в случае успешного сохранения
				.then((res: AxiosResponse<StatementModel>) =>
				{
					// выключаем спиннер
					this.modal.setShowModal(false);
					// если заявка не приехала, то ругаемся про неуспешное сохранение
					if (!res.data)
					{
						this.modal.setShowModal(true, ModalTypeEnum.Error, "Формирование заявления", "Формирование заявления завершилось ошибкой.");
						return;
					}
					// обновление статусов формы и подписи
					this.stat.setFormFileState(FileStateEnum.Succeed);
					this.stat.setSignFileState(FileStateEnum.Succeed);
					this.stat.setFormFileError("");
					this.stat.setSignFileError("");
					// записываем приехавший объект
					this.dataStore.setStatement(res.data);
					// парсим приехавший объект
					this.dataStore.UpdateSatementFromServer(false);
					// показать модалку с заявлением
					this.modal.setShowModal(true, ModalTypeEnum.StatementPreview, 'Заявление оператора ЭДО');
				})
				// в случае неуспешного сохранения
				.catch((res: AxiosError) =>
				{
					// выключаем спиннер
					this.modal.setShowModal(false);
					// Ругаемся
					if (res.response?.data)
					{
						let error = (res.response.data as ErrorResponse);
						this.modal.setShowModal(true, ModalTypeEnum.Error, "Сбой формирования заявления", error.MessageToUser + " (" + error.ActivityId + ")");
						return;
					}
					else this.modal.setShowModal(true, ModalTypeEnum.Error, "Формирование заявления", "Формирование заявления завершилось ошибкой.");
				});
			return;
		}

		if (this.StatementStatus === StatementStatusEnum.Created)
		{
			if (!this.register.IsValid || !this.info.IsValid || !this.files.IsValid) return;
			if (this.isStatementModified())
			{
				// Такого просто не может быть
				this.modal.setShowModal(true, ModalTypeEnum.Error, 'Данные изменились', 'Произошла непредвиденная ошибка');
				return;
			}

			this.modal.setShowModal(true, ModalTypeEnum.StatementSignSelector, 'Подписание заявления оператора ЭДО');
			return;
		}

		if (this.StatementStatus === StatementStatusEnum.Signed)
		{
			if (!this.register.IsValid || !this.info.IsValid || !this.files.IsValid) return;
			if (this.isStatementModified())
			{
				// Такого просто не может быть
				this.modal.setShowModal(true, ModalTypeEnum.Error, 'Данные изменились', 'Произошла непредвиденная ошибка');
				return;
			}

			this.modal.setShowModal(true, ModalTypeEnum.Spinner, 'Отправка заявления оператора ЭДО');
			this.dataStore.SendForm()
				// в случае успешной отправки
				.then((res: AxiosResponse<StatementModel>) =>
				{
					// выключаем спиннер
					this.modal.setShowModal(false);
					// если заявка не приехала, то ругаемся про неуспешное сохранение
					if (!res.data)
					{
						this.modal.setShowModal(true, ModalTypeEnum.Error, "Отправка заявления оператора ЭДО", "Отправка заявления завершилось ошибкой.");
						return;
					}
					// записываем приехавший объект
					this.dataStore.setStatement(res.data);
					// парсим приехавший объект
					this.dataStore.UpdateSatementFromServer(false);
					// Показываем подтверждение
					this.modal.setShowModal(true, ModalTypeEnum.Error, "Заявление оператора ЭДО отправлено", "Заявление оператора ЭДО отправлено на регистрацию в ФНС России.<br /><br />В течение 1 суток на адрес электронной почты для обратной связи, указанной Вами при регистрации, будет направлено уведомление о регистрации с идентификационным номером и датой регистрации обращения.");
					// включаем следилку за статусом
					this.dataStore.StartStatusHistoryUpdater();
				})
				// в случае неуспешной отправки
				.catch((res: AxiosError) =>
				{
					// выключаем спиннер
					this.modal.setShowModal(false);
					// Ругаемся
					if (res.response?.data)
					{
						let error = (res.response.data as ErrorResponse);
						this.modal.setShowModal(true, ModalTypeEnum.Error, "Сбой отправки заявления оператора ЭДО", error.MessageToUser + " (" + error.ActivityId + ")");
						return;
					}
					else this.modal.setShowModal(true, ModalTypeEnum.Error, "Сбой отправки заявления оператора ЭДО", "Отправка заявления завершилось ошибкой.");
				});
			return;
		}
	}

	// #endregion

	// #region Statement

	public StatementStatus: StatementStatusEnum = StatementStatusEnum.NotCreated;
	setStatementStatus(value: StatementStatusEnum)
	{
		this.StatementStatus = value;
		this.setIsDisabled(value === StatementStatusEnum.Sent || value === StatementStatusEnum.Received);
		this.register.setIsDisabled(this.IsDisabled);
		this.info.setIsDisabled(this.IsDisabled);
	}

	isStatementModified(): boolean
	{
		let resut: boolean = false;
		let files = new Map<string, FileLibrary>();
		this.files?.FileLists?.forEach((val) => { val.forEach((f) => files.set(f.FileId, f)); });
		if (
			this.dataStore?.Statement?.OrganizationName !== this.info?.OrganizationName?.Value
			|| this.dataStore?.Statement?.OrganizationInn !== this.info?.OrganizationInn?.Value
			|| this.dataStore?.Statement?.OrganizationKpp !== this.info?.OrganizationKpp?.Value
			|| this.dataStore?.Statement?.OrganizationHeadLastName !== this.info?.OrganizationHeadLastName?.Value
			|| this.dataStore?.Statement?.OrganizationHeadFirstName !== this.info?.OrganizationHeadFirstName?.Value
			|| this.dataStore?.Statement?.OrganizationHeadSecondName !== this.info?.OrganizationHeadSecondName?.Value
			|| this.dataStore?.Statement?.OrganizationHeadPosition !== this.info?.OrganizationHeadPosition?.Value
			|| this.dataStore?.Statement?.AgreementCheck !== this.info?.AgreementCheck?.Value
			|| this.dataStore?.Statement?.StatementInfos?.length !== this.register?.SelectedServiceCategories?.size
			|| this.dataStore?.Statement?.StatementType !== this.register?.StatementType
			|| this.dataStore?.Statement?.FeedbackEmail !== this.register?.FeedbackEmail?.Value
			|| this.dataStore?.Statement?.StatementFileInfos?.length !== files.size
		) return true;

		if (this.register?.StatementType === StatementTypeEnum.ClarifyInformation)
		{
			// сравниваем разнотипные даты регистрации
			let ds1 = this.dataStore?.Statement?.StatementPrevDate;
			let d2 = this.register?.OldRegisterDate.Value;
			let d1: Date | null;
			if (ds1 === '' || ds1 === undefined || ds1 === null) d1 = null;
			else d1 = new Date(ds1);
			if (d1 !== null && d2 === null) return true;
			if (d1 === null && d2 !== null) return true;
			if (d1 !== null && d2 !== null)
			{
				if (!DateFNS.isEqual(d1, d2)) return true;
			}

			if (
				(this.dataStore?.Statement?.StatementPrevNumber ?? "") !== (this.register?.OldRegisterNumber.Value ?? "")
				|| this.dataStore?.Statement?.Comments !== this.register?.Comment
			) return true;
		}

		this.dataStore?.Statement?.StatementInfos?.forEach((val) =>
		{
			let lib = this.register.SelectedServiceCategories.get(val.CategoryId);
			if (!lib)
			{
				resut = true;
				return;
			}
			if (
				lib.Email?.Value !== val.Email
				|| lib.Phone?.Value !== val.Phone
				|| lib.Web?.Value !== val.Href
				|| lib.Operation !== val.Operation
			)
			{
				resut = true;
				return;
			}
		});

		this.dataStore?.Statement?.StatementFileInfos?.forEach((val) =>
		{
			const lib = files.get(val.Id);
			if (!lib) 
			{
				resut = true;
				return;
			}
			if (
				lib.PageCount !== val.PageCount
				|| lib.FileName !== val.FileName
				|| lib.FileSize !== val.FileSize
				|| lib.FileTypeId !== val.FileTypeId
			)
			{
				resut = true;
				return;
			}
		});

		return resut;
	}

	// #endregion
}
